'use client';
import React, { useState } from 'react';
import Link from 'next/link';

interface FormData {
  clubName: string;
  aerodrome: string;
  icaoCode: string;
  cityRegion: string;
  contactName: string;
  email: string;
  phone: string;
  message: string;
}

export default function ContactGestionPVJMPage() {
  const [form, setForm] = useState<FormData>({
    clubName: '',
    aerodrome: '',
    icaoCode: '',
    cityRegion: '',
    contactName: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const buildEmailContent = () => {
    const subject = "Demande d'information — Application météo VFR";
    const body =
      `Nom du club / association / entreprise : ${form.clubName}\n` +
      `Nom du terrain ou aérodrome : ${form.aerodrome}\n` +
      `Code OACI ou identifiant : ${form.icaoCode}\n` +
      `Ville / région : ${form.cityRegion}\n` +
      `Nom de la personne contact : ${form.contactName}\n` +
      `Courriel : ${form.email}\n` +
      `Téléphone : ${form.phone}\n\n` +
      `Message / besoin particulier :\n${form.message}`;
    return { subject, body };
  };

  const buildMailtoLink = () => {
    const { subject, body } = buildEmailContent();
    return `mailto:gestionpvjm@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const mailtoLink = buildMailtoLink();
    const link = document.createElement('a');
    link.href = mailtoLink;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenGmail = () => {
    const { subject, body } = buildEmailContent();
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=gestionpvjm@gmail.com&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank', 'noopener,noreferrer');
  };

  const [copied, setCopied] = useState(false);

  const handleCopyMessage = () => {
    const { subject, body } = buildEmailContent();
    const fullText = `À : gestionpvjm@gmail.com\nObjet : ${subject}\n\n${body}`;
    navigator.clipboard.writeText(fullText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div className="bg-slate-50" style={{ fontFamily: 'var(--font-plus-jakarta-sans, sans-serif)', height: '100dvh', overflowY: 'auto' }}>
      {/* Header bar */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <Link
          href="/"
          className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 transition-colors text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
          </svg>
          Retour à l&apos;application météo
        </Link>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">

        {/* Title */}
        <div>
          <h1 className="text-xl font-bold text-slate-800 leading-tight">
            Application météo VFR pour clubs, associations et entreprises
          </h1>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            Gestion PVJM peut adapter cette application météo VFR à d&apos;autres clubs, associations aéronautiques,
            entreprises ou terrains d&apos;aviation.
          </p>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            L&apos;application peut regrouper, selon les données disponibles :
          </p>
          <ul className="mt-2 space-y-1 text-sm text-slate-600 pl-4">
            {[
              'Météo VFR',
              'METAR décodé',
              'Prévisions TAF simplifiées',
              'Pistes et vent',
              'Visibilité',
              'Plafond',
              'Caméras météo disponibles',
              'Informations locales propres au terrain',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-400 shrink-0"/>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Pricing */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Tarification</h2>
          <p className="text-sm text-slate-700 font-medium">
            Application météo VFR de base à partir de <span className="text-slate-900 font-bold">1 $ par jour</span>, soit 365 $ par année.
          </p>
          <p className="mt-2 text-xs text-slate-500 leading-relaxed">
            Cette formule de base comprend une application simple avec 3 pages au choix parmi les onglets disponibles : Résumé, Pistes, Météo, Vigilance, METAR ou Caméras.
          </p>
          <p className="mt-2 text-xs text-slate-500 leading-relaxed">
            Les pages supplémentaires, demandes particulières, intégrations locales, caméras privées, stations météo personnelles, personnalisation avancée ou développements spécifiques peuvent être évalués séparément.
          </p>
        </div>

        {/* Important note */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-xs text-amber-800 leading-relaxed">
            <span className="font-semibold">Note importante : </span>
            Cette application demeure un outil indicatif d&apos;aide à la consultation. Elle ne remplace pas les sources officielles,
            les METAR, TAF, NOTAM, CFS, cartes météo, procédures locales ni le jugement du pilote.
          </p>
        </div>

        {/* Contact form */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Formulaire de contact</h2>
          <form onSubmit={handleSubmit} className="space-y-4">

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Nom du club, association ou entreprise <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="clubName"
                value={form.clubName}
                onChange={handleChange}
                required
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent"
                placeholder="Ex : Aéroclub de Chicoutimi"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Nom du terrain ou aérodrome <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="aerodrome"
                value={form.aerodrome}
                onChange={handleChange}
                required
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent"
                placeholder="Ex : Aéroport de Saint-Honoré"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Code OACI ou identifiant
                </label>
                <input
                  type="text"
                  name="icaoCode"
                  value={form.icaoCode}
                  onChange={handleChange}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent uppercase"
                  placeholder="Ex : CYRC"
                  maxLength={6}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Ville / région
                </label>
                <input
                  type="text"
                  name="cityRegion"
                  value={form.cityRegion}
                  onChange={handleChange}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent"
                  placeholder="Ex : Saguenay"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Nom de la personne contact <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="contactName"
                value={form.contactName}
                onChange={handleChange}
                required
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent"
                placeholder="Prénom et nom"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Courriel <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent"
                  placeholder="votre@courriel.com"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Téléphone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent"
                  placeholder="Ex : 418 555-0000"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Message / besoin particulier
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={4}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent resize-none"
                placeholder="Décrivez votre besoin, vos équipements disponibles (station météo, caméras), ou toute demande particulière…"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-slate-700 hover:bg-slate-800 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-colors"
            >
              Envoyer par courriel
            </button>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleOpenGmail}
                className="w-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" fill="#EA4335"/>
                </svg>
                Ouvrir dans Gmail
              </button>
              <button
                type="button"
                onClick={handleCopyMessage}
                className="w-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {copied ? (
                  <>
                    <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span className="text-green-600">Copié !</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                    Copier le message
                  </>
                )}
              </button>
            </div>

            <p className="text-xs text-slate-500 text-center leading-relaxed">
              Si votre logiciel de courriel ne s&apos;ouvre pas, écrivez-nous directement à{' '}
              <a
                href="mailto:gestionpvjm@gmail.com"
                className="text-slate-600 hover:text-slate-800 underline underline-offset-2 transition-colors"
              >
                gestionpvjm@gmail.com
              </a>
            </p>
          </form>
        </div>

        {/* Back link bottom */}
        <div className="text-center pb-12">
          <Link
            href="/"
            className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2 transition-colors"
          >
            ← Retour à l&apos;application météo
          </Link>
        </div>

      </div>
    </div>
  );
}
